import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ExternalLink, Layout, Minimize2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export type EmbedToolbarMode = 'minimal' | 'standard';
export type EmbedHeightPreset = '50vh' | '70vh' | '90vh';

export interface GoogleDocsEmbedEditorProps {
  /** URL para edição no iframe (ex: https://docs.google.com/document/d/ID/edit ou com ?rm=minimal) */
  embedEditUrl: string;
  title: string;
  onClose: () => void;
  /** Se true, permite alterar toolbar e altura */
  showLayoutOptions?: boolean;
  /** E-mail do usuário logado: força o Google a usar essa conta no embed (authuser) e evita conflito com outras contas no navegador */
  userEmail?: string;
}

/**
 * Editor do Google Docs embedado (iframe). Opções de layout: barra minimal/padrão e altura.
 * NÃO abre em nova aba — tudo na mesma tela.
 */
export function GoogleDocsEmbedEditor({
  embedEditUrl,
  title,
  onClose,
  showLayoutOptions = true,
  userEmail,
}: GoogleDocsEmbedEditorProps) {
  const [toolbarMode, setToolbarMode] = useState<EmbedToolbarMode>('minimal');
  const [heightPreset, setHeightPreset] = useState<EmbedHeightPreset>('70vh');
  /** Força um segundo carregamento do iframe após a montagem; o 2º load costuma exibir o doc atualizado na conta correta */
  const [iframeLoadKey, setIframeLoadKey] = useState(0);

  const url = new URL(embedEditUrl);
  if (toolbarMode === 'minimal') {
    url.searchParams.set('rm', 'minimal');
  } else {
    url.searchParams.delete('rm');
  }
  if (userEmail?.trim()) {
    url.searchParams.set('authuser', userEmail.trim());
  }
  const iframeSrc = url.toString();

  const heightMap: Record<EmbedHeightPreset, string> = {
    '50vh': '50vh',
    '70vh': '70vh',
    '90vh': '90vh',
  };
  const iframeHeight = heightMap[heightPreset];

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollBeforeFocusRef = useRef(0);

  // Após o primeiro carregamento, forçar um reload do iframe para pegar o documento atualizado
  useEffect(() => {
    const t = setTimeout(() => {
      setIframeLoadKey((k) => k + 1);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  // Mantém a última posição de scroll conhecida (para restaurar quando o iframe ganha foco)
  useEffect(() => {
    const onScroll = () => {
      scrollBeforeFocusRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Ao clicar no iframe, guardamos a posição de scroll atual (antes do foco mudar)
  const onIframeContainerMouseDown = (e: React.MouseEvent) => {
    if (e.target === iframeRef.current) {
      scrollBeforeFocusRef.current = window.scrollY;
    }
  };

  // Quando o iframe ganha foco (ex.: clique dentro), o navegador pode scrollar a página para o topo — restauramos a posição
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const onBlur = () => {
      // Só restaura se o foco foi para o nosso iframe (evita restaurar ao trocar de aba)
      if (document.activeElement === iframeRef.current) {
        const y = scrollBeforeFocusRef.current;
        timeoutId = setTimeout(() => {
          window.scrollTo(0, y);
        }, 10);
      }
    };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <CardTitle className="text-lg">{title}</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          {showLayoutOptions && (
            <>
              <div className="flex items-center gap-1.5">
                <Layout className="h-4 w-4 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Barra:</Label>
                <Select
                  value={toolbarMode}
                  onValueChange={(v) => setToolbarMode(v as EmbedToolbarMode)}
                >
                  <SelectTrigger className="w-[130px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal (reduzida)</SelectItem>
                    <SelectItem value="standard">Padrão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5">
                <Minimize2 className="h-4 w-4 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Altura:</Label>
                <Select
                  value={heightPreset}
                  onValueChange={(v) => setHeightPreset(v as EmbedHeightPreset)}
                >
                  <SelectTrigger className="w-[100px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50vh">50%</SelectItem>
                    <SelectItem value="70vh">70%</SelectItem>
                    <SelectItem value="90vh">90%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <Button variant="outline" size="sm" asChild>
            <a href={iframeSrc} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1.5" />
              Abrir no Google Docs
            </a>
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar editor
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Alert variant="default" className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
          <AlertDescription>
            O editor incorporado usa a conta Google do navegador. Se estiver logado com outra conta e o documento pedir acesso ou autenticação, use <strong>Abrir no Google Docs</strong> acima e selecione a conta com que você entrou no Asseto. Em modo anônimo/privado o Google pode bloquear o acesso.
          </AlertDescription>
        </Alert>
        <div
          className="rounded-lg border overflow-hidden bg-muted/30"
          style={{ minHeight: iframeHeight }}
          onMouseDown={onIframeContainerMouseDown}
        >
          <iframe
            key={iframeLoadKey}
            ref={iframeRef}
            title={title}
            src={iframeSrc}
            className="w-full border-0"
            style={{ minHeight: iframeHeight, height: iframeHeight }}
            allow="clipboard-read; clipboard-write"
          />
        </div>
      </CardContent>
    </Card>
  );
}
